import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type CellContext } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { cardMessages } from 'src/features/cards/utils/messages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/utils/messages';

import type { CashCollections } from 'src/features/cashCollections/types/cashCollections';

interface CellCashCollectionActionsProps<TData, TValue> extends CellContext<TData, TValue> {
    singleRowSelection: (rowId: string) => void;
    sidePanelName: string;
    editCallback?: (version: TData) => void;
    deleteCallback?: (version: TData) => void;
}

function CellCashCollectionActions({
    sidePanelName,
    singleRowSelection,
    row,
    editCallback,
    deleteCallback,
}: CellCashCollectionActionsProps<CashCollections, unknown>) {
    const { formatMessage } = useIntl();
    const { openPanel } = useSidePanel();
    const permissionCashCollections = useHasPermissions([PERMISSION.CASH_COLLECTIONS]);

    const handleSidePanelOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        singleRowSelection(row.id.toString());
        openPanel(sidePanelName);
    };

    return (
        <div className="flex gap-1">
            {permissionCashCollections && (
                <>
                    {editCallback && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button onClick={() => editCallback!(row.original)} size="icon" variant="link">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} className="cursor-pointer" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">{formatMessage(generalMessages.edit)}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    {deleteCallback && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button onClick={() => deleteCallback!(row.original)} size="icon" variant="link">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} className="cursor-pointer" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">{formatMessage(generalMessages.delete)}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </>
            )}
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button onClick={handleSidePanelOpen} size="icon" variant="link">
                            <FontAwesomeIcon icon={ICON_MAP.SIDE_PANEL} className="cursor-pointer" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{formatMessage(cardMessages.openSidePanel)}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}

export { CellCashCollectionActions };
